// The Forecasting tab's Overview (2026-10-29, Patrick's ask). Two tables over the last complete weeks:
//   1. Are we short overall?  every function's work (Phone, Chat, Email, Support) as FTE on his 23% method,
//                              the Seniors' measured CXHub help, the scheduled FTE of the teams that do customer
//                              work, and the gap before and after that help.
//   2. Who does what          a work matrix: for each team, the Bright Pattern handle time by channel and the
//                              CXHub handle time by account group, beside the CXHub time it was scheduled for.
// All the arithmetic is on the server (server/src/services/forecasting-overview.js); this only lays it out.
// Hours sit behind a toggle, as on the other views.
import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableHead, TableRow,
} from '@mui/material'
import { fetchForecastOverview } from '../api.js'
import SectionHeading from './SectionHeading.jsx'
import { Cell, Head, WeekCell, Fte, Divider, rowSx } from './ForecastTableParts.jsx'
import { formatGap, gapTone, weekLabel, EM_DASH } from '../forecastPhoneChat.js'

const TABLE_BOX = { width: 'fit-content', maxWidth: '100%' }

// Matrix hours per week: whole hours, a dash with no data.
const formatH = (h) => (typeof h === 'number' && Number.isFinite(h) ? `${Math.round(h).toLocaleString('en-US')}` : EM_DASH)

function StaffingTable({ report, showHours }) {
  const a = report.assumptions
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={5} left>FTE needed (× {1 + a.shrinkageRate})</Head>
            <Head rowSpan={2} left>Seniors' help</Head>
            <Head rowSpan={2} left>FTE scheduled (÷ {a.scheduledHoursPerFte})</Head>
            <Head colSpan={2} left>Gap</Head>
          </TableRow>
          <TableRow>
            <Head left>Phone</Head>
            <Head>Chat</Head>
            <Head>Email</Head>
            <Head>Support</Head>
            <Head>Total</Head>
            <Head left>Before help</Head>
            <Head>After help</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={10}>Complete weeks · oldest first</Divider>
          {report.weeks.map((row) => {
            const n = row.need
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={!n.phone}><Fte value={n.phone} showHours={showHours} /></Cell>
                <Cell muted={!n.chat}><Fte value={n.chat} showHours={showHours} /></Cell>
                <Cell muted={!n.email}><Fte value={n.email} showHours={showHours} /></Cell>
                <Cell muted={!n.support}><Fte value={n.support} showHours={showHours} /></Cell>
                <Cell bold muted={!n.total}><Fte value={n.total} showHours={showHours} /></Cell>
                <Cell left muted={!n.help}><Fte value={n.help} showHours={showHours} /></Cell>
                <Cell left bold muted={!row.supply}><Fte value={row.supply} showHours={showHours} /></Cell>
                <Cell left bold tone={gapTone(row.gap.beforeHelp)} muted={row.gap.beforeHelp == null}>{formatGap(row.gap.beforeHelp)}</Cell>
                <Cell bold tone={gapTone(row.gap.afterHelp)} muted={row.gap.afterHelp == null}>{formatGap(row.gap.afterHelp)}</Cell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}

function MatrixTable({ matrix }) {
  const t = matrix.totals
  const line = (r, bold = false) => (
    <TableRow key={r.team}>
      <Cell align="left" bold={bold}>{r.team}</Cell>
      <Cell left bold={bold} muted={!r.bpPhone}>{formatH(r.bpPhone)}</Cell>
      <Cell bold={bold} muted={!r.bpChat}>{formatH(r.bpChat)}</Cell>
      <Cell bold={bold} muted={!r.bpEmail}>{formatH(r.bpEmail)}</Cell>
      <Cell left bold={bold} muted={!r.cxhubCore}>{formatH(r.cxhubCore)}</Cell>
      <Cell bold={bold} muted={!r.cxhubSupport}>{formatH(r.cxhubSupport)}</Cell>
      <Cell bold={bold} muted={!r.cxhubHandled}>{formatH(r.cxhubHandled)}</Cell>
      <Cell left bold={bold} muted={!r.cxhubScheduled}>{formatH(r.cxhubScheduled)}</Cell>
      <Cell muted>{formatH(r.bpScheduled)}</Cell>
    </TableRow>
  )
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Team</Head>
            <Head colSpan={3} left>Bright Pattern handle time (raw)</Head>
            <Head colSpan={3} left>CXHub handle time</Head>
            <Head colSpan={2} left>Scheduled</Head>
          </TableRow>
          <TableRow>
            <Head left>Phone</Head>
            <Head>Chat</Head>
            <Head>Email</Head>
            <Head left>1A, AM (eBay, Website)</Head>
            <Head>Support</Head>
            <Head>All handled</Head>
            <Head left>CXHub blocks</Head>
            <Head>Bright Pattern queue</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={9}>
            Hours a week, average of the {matrix.weeks.length} complete weeks ending {weekLabel(matrix.weeks[0])} to {weekLabel(matrix.weeks[matrix.weeks.length - 1])}
          </Divider>
          {matrix.rows.map((r) => line(r))}
          {line({ team: 'All teams', ...t }, true)}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The loaded view, apart from the fetch so it can be rendered from a report alone.
export function ForecastOverviewView({ report }) {
  const [showHours, setShowHours] = useState(false)
  const a = report.assumptions
  return (
    <Box>
      <SectionHeading variant="subtitle1" sx={{ mb: 1, ...TABLE_BOX }} title="Forecasting: Overview" descriptionMaxWidth={1100}>
        Are we short overall? One line per complete week: all the work the four views count (Phone, Chat, Email, Support) against the scheduled
        hours of every team that does customer work. Nothing is allocated between functions, so each scheduled hour is counted once and the
        help teams give each other nets out. If the total is balanced, the gaps in the other views are mostly cross-help; if it is short, the
        shortage is real. Complete weeks only, because a part week's work against a full week's schedule would read as a surplus.
      </SectionHeading>
      <FormControlLabel
        control={<Switch size="small" checked={showHours} onChange={(e) => setShowHours(e.target.checked)} />}
        label="Show hours under each FTE"
        sx={{ mb: 1 }}
      />
      {(report.failures ?? []).length > 0 && (
        <Alert severity="error" sx={{ mb: 1, maxWidth: 1100 }}>
          Some of this page could not be read just now, so it is partly empty. Reload to try again.
          {report.failures.map((f) => (
            <Box key={f.section} component="div">{f.section}: {f.message}</Box>
          ))}
        </Alert>
      )}
      {report.warnings.includes('need_incomplete') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          A week is missing some function's work, so it has no total or gap. The functions that are there are still shown.
        </Alert>
      )}
      {report.warnings.includes('per_rep_unavailable') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          The per-agent handle times could not be read just now, so the Seniors' help and the CXHub columns of the matrix are missing. Reload to try again.
        </Alert>
      )}

      <Box sx={{ mt: 2, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="1. Are we short overall?" descriptionMaxWidth={1100}>
          Need is each view's own work hours (the Phone view's calls and Spanish callbacks, chats, Email, and Support with its phone work), as FTE
          = hours ÷ {a.onQueueHoursPerFte} × {1 + a.shrinkageRate}, your {Math.round(a.shrinkageRate * 100)}% standard with no flat spare. The
          calls to Support's TRQ Inbound Voice queue are in the Phone view's call count and in Support's need, so they come off Phone here and
          stay in Support. Scheduled is the scheduled hours of {a.supplyTeams.join(', ')} ÷ {a.scheduledHoursPerFte} (paid headcount: the schedule
          includes the 0.5 h unpaid lunch). The Seniors are not scheduled supply, because they work CXHub in project and loan state: their help
          is measured from the CXHub contacts they handled (contacts × handle time) and comes off the work before the markup, since unscheduled
          hours carry no shrinkage. Their project and loan time is not used, because it ran about twice what they actually handled. The gap is
          shown before that help, which is the honest staffing signal, and after it, which shows how much of the shortage the Seniors close and
          how exposed the plan is when they are away.
        </SectionHeading>
        <StaffingTable report={report} showHours={showHours} />
      </Box>

      {report.matrix && (
        <Box sx={{ mt: 3, ...TABLE_BOX }}>
          <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="2. Who does what" descriptionMaxWidth={1100}>
            The cross-help, from actuals. Bright Pattern columns are Schedule Hub's raw handle time by the team of the agent who handled the
            contact (a transferred contact counts once per leg, and raw time includes time the agent was not looking at an email, so it runs
            above the in-focus time the views use for the need). CXHub columns are contacts × handle time by agent, each agent in their team by
            name: 1A and AM eBay and Website email apart from Support email. Scheduled is the team's CXHub blocks and its Bright Pattern queue
            time, per week. Compare "All handled" with "CXHub blocks" to see how much of the team's CXHub time went to work that is not its
            own, and how much was done outside the schedule (the Seniors have no blocks). Management and anyone the roster does not list are
            shown apart so the totals add up.
          </SectionHeading>
          {report.warnings.includes('bp_team_missing') && (
            <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>Schedule Hub returned no Bright Pattern handle time by team, so those columns are empty.</Alert>
          )}
          <MatrixTable matrix={report.matrix} />
        </Box>
      )}

      <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mt: 2 }} title="Colors and notes" descriptionMaxWidth={1100}>
        Colors: gap red below 0, amber under +1. "—" means no data, never zero. Per-agent numbers are claimed time, not effort, and are not a
        performance measure; they are grouped by team here, never shown by person.
      </SectionHeading>
    </Box>
  )
}

export default function ForecastOverview() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    fetchForecastOverview()
      .then((r) => { if (live) setReport(r) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>
  if (!report) return null
  return <ForecastOverviewView report={report} />
}
